import { Fragment } from "react";
import type { IAccountingDataListRows } from "types/accountingData";
import { useFormatCurrency } from "utils/customHooks/useFormatCurrency";

import type { Currency } from "@krea/shared/types/common";
import { cn } from "@krea/shared/utils";

import type { IAccountingDataResponse } from "@/features/lender-applications/types/lender-application";

const valueFormat = (
  value: number,
  field: string,
  defaultCurrencyFormatter: (
    value: number | null | undefined,
    currencyToOverride?: string | undefined,
  ) => string,
) => {
  const currentValue =
    typeof value === "number" && value < 0 ? Math.abs(value) : value; // fix for PDF, otherwise PDF-file displays incorrectly negative numbers
  const str = defaultCurrencyFormatter(currentValue);

  switch (true) {
    case field === "operatingMargin":
    case field === "quickRatio":
    case field === "solvency":
    case field === "interestCoverageRatio":
    case field === "profitRatio":
    case field === "workingCapital":
      return `${(value * 100).toFixed(2)} %`; // or return `${+(value * 100).toFixed(2)} %`;
    default:
      return typeof value === "number" && value < 0 ? `-${str}` : str; // fix for PDF, otherwise PDF-file displays incorrectly negative numbers
  }
};

const AccountingDataTable = ({
  data,
  rows,
  currency,
}: {
  data:
    | IAccountingDataResponse["balanceSheetEntries"]
    | IAccountingDataResponse["profitLossEntries"]
    | IAccountingDataResponse["financialRatioEntries"];
  rows: IAccountingDataListRows[];
  currency: Currency | undefined;
}) => {
  const [formatterWithCurrency] = useFormatCurrency({ currency });

  return (
    <div className="overflow-x-auto bg-white px-3 pb-6 shadow-sm">
      <table className="mb-0 w-full border-separate border-spacing-0 text-sm">
        <tbody>
          {rows.map((row, index) => (
            <Fragment key={index}>
              {index === 0 && (
                <tr>
                  <td />
                  {data.map((item, index) => (
                    <td
                      key={index}
                      className="m-0 border-y-0 py-4 text-right text-[1.25rem] font-bold"
                    >
                      <div className="whitespace-nowrap">{item.period}</div>
                    </td>
                  ))}
                </tr>
              )}
              {!row.title && row === rows[0] && (
                <tr>
                  <td className="py-1" colSpan={data.length + 1} />
                </tr>
              )}
              {row.title && (
                <Fragment>
                  {row !== rows[0] && (
                    <tr>
                      <td className="py-5" colSpan={data.length + 1} />
                    </tr>
                  )}
                  <tr>
                    <td className="pt-0" colSpan={data.length + 1}>
                      <h5 className="m-0 mb-1">{row.title}</h5>
                    </td>
                  </tr>
                </Fragment>
              )}
              {row.rows.map((item, index) => (
                <tr
                  key={index}
                  className={cn("transition-colors hover:bg-gray-200", {
                    "bg-gray-100": index % 2 === 0,
                  })}
                >
                  <td
                    className={cn("border-t border-b p-2", {
                      "text-[1.25rem] m-0 font-bold": item.isBold,
                      "border-t-1 border-t-gray-500":
                        item === row.rows[0],
                      "border-b-2 border-b-gray-500":
                        item === row.rows[row.rows.length - 1],
                    })}
                  >
                    {item.value}
                  </td>
                  {data.map((d, index) => (
                    <td
                      key={index}
                      className={cn(
                        "border-t border-b text-right align-middle border-l border-r whitespace-nowrap p-2",
                        {
                          "text-[1.25rem] m-0 font-bold": item.isBold,
                          "border-t-1 border-t-gray-500":
                            item === row.rows[0],
                          "border-b-2 border-b-gray-500":
                            item === row.rows[row.rows.length - 1],
                        },
                      )}
                    >
                      {(() => {
                        const value = (d as Record<string, unknown>)[
                          item.field
                        ];

                        return value !== null &&
                          value !== undefined &&
                          typeof value === "number"
                          ? valueFormat(
                              value,
                              item.field,
                              formatterWithCurrency,
                            )
                          : "-";
                      })()}
                    </td>
                  ))}
                </tr>
              ))}
            </Fragment>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const AccountingDataList = ({
  data,
  rows,
  className,
  header,
  currency,
}: {
  data:
    | IAccountingDataResponse["balanceSheetEntries"]
    | IAccountingDataResponse["profitLossEntries"]
    | IAccountingDataResponse["financialRatioEntries"];
  rows: IAccountingDataListRows[];
  className?: string;
  header: string;
  currency: Currency | undefined;
}) => (
  <section className={cn(className)}>
    <h2 className="mb-5 text-[1.5rem]">{header}</h2>
    <AccountingDataTable data={data} rows={rows} currency={currency} />
  </section>
);

export default AccountingDataList;
